import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    Dialog, DialogTitle, DialogContent, IconButton, CircularProgress, Box
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useSnackbar } from '@contexts/SnackbarContext';
import API from '@utils/axios';
import { formError } from '@utils/helpers';
import SendoutCreationForm from '@components/sendout/SendoutCreationForm';
import SendoutRecapStep from '@components/sendout/SendoutRecapStep';

function SendoutCreationDialog({ modalCreateOpen, setModalCreateOpen }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showMessage } = useSnackbar();
    const form = useRef();
    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState({
        workflow_id: '',
        mailing_list_id: '',
        status: 'InProgress',
        description: '',
    });
    const [processing, setProcessing] = useState(false);

    const handleClose = () => {
        setModalCreateOpen(false);
        setActiveStep(0);
    }

    const handleNext = (data) => {
        setFormData(data);
        setActiveStep(1);
    };

    const handleBack = () => {
        setActiveStep(0);
    };

    const handleSubmit = async () => {
        try {
            setProcessing(true);
            const result = await API.post(`/sendouts`, formData);
            showMessage(t('Sendout created successfully'), 'success');
            setProcessing(false);
            setModalCreateOpen(false);
            navigate(`/sendouts/${result.data.id}`);
        } catch (error) {
            if (error.response.data.errors.workflow_id) {
                formError(error, t, form.current.setError, showMessage, 'This workflow contains errors: {{msg}}');
            } else {
                formError(error, t, form.current.setError, showMessage);
            }
            setProcessing(false);
        }
    };

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <SendoutCreationForm 
                        ref={form} 
                        formData={formData} 
                        fieldsVariant="outlined" 
                        onSubmit={handleNext}
                    />
                );
            case 1:
                return (
                    <SendoutRecapStep
                        formData={formData}
                        onBack={handleBack}
                        onSubmit={handleSubmit}
                        processing={processing}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Dialog
            open={modalCreateOpen}
            onClose={handleClose}
            aria-labelledby="parent-modal-title"
        >
            <DialogTitle id="customized-dialog-title">
                {t('Start a new sendout')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent sx={{ width: '420px', pt: 0 }}>
                {processing && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <CircularProgress />
                    </Box>
                )}
                <Box sx={{ display: processing ? 'none' : 'block' }}>
                    {renderStepContent(activeStep)}
                </Box>
            </DialogContent>
        </Dialog>
    );
}

export default SendoutCreationDialog;